import Cookies from 'js-cookie';
import { getAuthCookieOptions } from 'r3ui-lib';
import { navigateTo, useRuntimeConfig } from '#imports';

export type ApiFetchAuthContext = 'protected' | 'public';

export type ApiFetchOptions = NonNullable<
  Parameters<ReturnType<typeof $fetch.create>>[1]
> & {
  authContext?: ApiFetchAuthContext;
};

export function handleUnauthorizedResponse(): void {
  const { public: publicConfig } = useRuntimeConfig();
  Cookies.remove(
    'auth_token',
    getAuthCookieOptions({
      origin: publicConfig.origin as string | undefined,
      authCookieDomain: publicConfig.authCookieDomain as string | undefined,
    })
  );
  navigateTo('/login', { external: true });
}

export function fatalErrorHandler(response: Response): void {
  if (response.status === 401) {
    handleUnauthorizedResponse();
  }
}

function createApiFetchClient(authContext: ApiFetchAuthContext) {
  const config = useRuntimeConfig();
  const locale = Cookies.get('i18n_redirected');
  const token = Cookies.get('auth_token');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (locale) headers['Accept-Language'] = locale;
  if (token) headers.Authorization = `Bearer ${token}`;

  return $fetch.create({
    baseURL: `${config.public.apiUrl}`,
    headers,
    onResponseError({ response }) {
      if (authContext === 'protected' && response.status === 401) {
        handleUnauthorizedResponse();
      }
    },
  });
}

export async function apiFetch<T>(request: string, options?: ApiFetchOptions) {
  const authContext = options?.authContext ?? 'protected';
  const client = createApiFetchClient(authContext);
  const { authContext: _authContext, ...fetchOptions } = options ?? {};
  return await client<T>(request, fetchOptions);
}

/** Заголовки таблицы используют текущий язык и заново читают токен перед запросом. */
export function createTableRequestHeaders(
  source: HeadersInit | undefined,
  locale: string
): Headers {
  const headers = new Headers(source);
  headers.set('Accept', 'application/json');
  headers.set('Accept-Language', locale);
  const token = Cookies.get('auth_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  else headers.delete('Authorization');
  return headers;
}

export function tableTransportOptions() {
  return {
    onUnauthorized: fatalErrorHandler,
    onError: (error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('[table-error]', error);
    },
    notFound: 'error' as const,
  };
}
